import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type {
  Pedido,
  PedidoComItens,
  PedidoListagem,
  StatusPedido,
  GatewayPagamento,
  OrdenacaoPedido,
  PedidoEvento,
  NovoPedidoEvento,
  DetalheOperacionalPedido,
  ConfirmacaoPagamentoManual,
  EnvioPedido,
  RastreioPedido,
  SalvarRastreioEntrada,
} from '@pgcustom/shared-types';

/** Filtros aceitos por `GET /pedidos` (`listarPedidosQuerySchema`, `apps/api/src/modules/pedidos/pedidos.schema.ts`). */
export interface FiltroListaPedidos {
  status?: StatusPedido;
  clienteId?: string;
  busca?: string;
  gateway?: GatewayPagamento;
  origem?: string;
  criadoDesde?: string;
  ordenar?: OrdenacaoPedido;
  limit?: number;
  offset?: number;
}

/**
 * Adapter do módulo Pedidos no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/pedidos/*`
 * (`apps/api/src/modules/pedidos/pedidos.routes.ts`). Mesmo padrão de
 * `ClientesApi`: `listar` devolve `Resultado<T>` (alimenta estados de
 * UI diretamente); `obterPorId`/`pagar`/`cancelar` propagam `ApiError`
 * — quem decide como exibir a falha é a tela/modal que chama.
 */
export class PedidosApi {
  constructor(private readonly api: ApiClient) {}

  async listar(filtro: FiltroListaPedidos): Promise<Resultado<PedidoListagem[]>> {
    try {
      const resposta = await this.api.get<{ pedidos: PedidoListagem[] }>('/pedidos', { ...filtro });
      if (resposta.pedidos.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.pedidos };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return {
        status: 'erro',
        mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar pedidos',
      };
    }
  }

  async obterPorId(id: string): Promise<PedidoComItens> {
    const resposta = await this.api.get<{ pedido: PedidoComItens }>(`/pedidos/${id}`);
    return resposta.pedido;
  }

  async pagar(id: string, gateway: GatewayPagamento): Promise<Pedido> {
    const resposta = await this.api.post<{ pedido: Pedido }>(`/pedidos/${id}/pagar`, { gateway });
    return resposta.pedido;
  }

  async cancelar(id: string, motivo: string): Promise<Pedido> {
    const resposta = await this.api.post<{ pedido: Pedido }>(`/pedidos/${id}/cancelar`, { motivo });
    return resposta.pedido;
  }

  /** Pagamento recebido fora do fluxo automático (PIX direto, dinheiro, transferência etc.). */
  async confirmarPagamentoManual(id: string, dado: ConfirmacaoPagamentoManual): Promise<Pedido> {
    const resposta = await this.api.post<{ pedido: Pedido }>(`/pedidos/${id}/pagamento-manual`, dado);
    return resposta.pedido;
  }

  /** Timeline de comunicação (mensagem ao cliente + nota interna) — `POST /pedidos/:id/eventos`. */
  async adicionarEvento(id: string, dado: NovoPedidoEvento): Promise<PedidoEvento> {
    const resposta = await this.api.post<{ evento: PedidoEvento }>(`/pedidos/${id}/eventos`, dado);
    return resposta.evento;
  }

  /** Visão ADMINISTRATIVA completa (inclui `notaInterna`) — `GET /pedidos/:id/eventos`. */
  async listarEventos(id: string): Promise<PedidoEvento[]> {
    const resposta = await this.api.get<{ eventos: PedidoEvento[] }>(`/pedidos/${id}/eventos`);
    return resposta.eventos;
  }

  async arquivar(id: string): Promise<Pedido> {
    const resposta = await this.api.post<{ pedido: Pedido }>(`/pedidos/${id}/arquivar`, {});
    return resposta.pedido;
  }

  async restaurar(id: string): Promise<Pedido> {
    const resposta = await this.api.post<{ pedido: Pedido }>(`/pedidos/${id}/restaurar`, {});
    return resposta.pedido;
  }

  /** Composição só-leitura (OPS/Envio/Comissão) — `GET /pedidos/:id/detalhe-operacional`. */
  async obterDetalheOperacional(id: string): Promise<DetalheOperacionalPedido> {
    const resposta = await this.api.get<{ detalheOperacional: DetalheOperacionalPedido }>(
      `/pedidos/${id}/detalhe-operacional`
    );
    return resposta.detalheOperacional;
  }

  /**
   * PDF real do documento do pedido — `GET /pedidos/:id/documento?tipo=...`.
   * `official` = Orçamento (vira OPS quando pago); `production` = checklist de bancada.
   * Renderizado ao vivo pelo motor de documentos do site legado, repassado pelo ERP.
   * Lança `ApiError` 503 quando a integração não está configurada, 404 quando o pedido/
   * documento não existe.
   */
  async baixarDocumento(id: string, tipo: 'official' | 'production'): Promise<Blob> {
    return this.api.baixarBlob(`/pedidos/${id}/documento`, { tipo });
  }

  /**
   * ENVIO E RASTREAMENTO — lê da ponte com o site (`shipping_labels`). Lança `ApiError` 503
   * quando `SITE_SERVICE_TOKEN` não está configurada no ERP, 502 quando o site não responde.
   */
  async obterRastreio(id: string): Promise<EnvioPedido> {
    return this.api.get<EnvioPedido>(`/pedidos/${id}/rastreio`);
  }

  /** Grava o rastreio do cliente via a ponte. O upsert e a idempotência do e-mail ficam no site. */
  async salvarRastreio(id: string, dado: SalvarRastreioEntrada): Promise<RastreioPedido | null> {
    const resposta = await this.api.patch<{ rastreio: RastreioPedido | null }>(`/pedidos/${id}/rastreio`, dado);
    return resposta.rastreio;
  }
}
